/**
 * 粒子化：照片 ⇄ 星尘（docs/prototype-v0.1.md §4.1，着色器和节奏同原型）。
 *
 * 原型里照片是星海里的三维卡片，星尘在卡片周围散开再凝聚。正式版的照片在网页面板里，
 * 所以星尘画在盖住全屏的一块透明画布上（第三个 WebGL 上下文，第一次用时才建，平时藏起来）：
 * - 打开：星尘从被点的那颗星涌出，绕着漩涡飞进面板的画框，凝聚成照片（2 秒）；
 *   清晰的照片再带着发光的边烧出来（0.8 秒），星尘淡去（0.9 秒），最后换回面板里的图片。
 * - 关上：照片带着发光的边烧掉（0.35 秒），星尘像天灯一样升起飘散（1.7 秒）。
 * - 擦掉一段字（入场爱心炸开后的那段话）：从左往右烧过去（1.4 秒），擦过的地方化成星尘升起（2.4 秒散完）。
 *   字画在透明的画布上：透明的地方没有星尘，照片不透明，不受影响。
 * 每颗粒子对应照片上的一个格点（桌面 128×128、手机 96×96），颜色从照片里取。
 */
import * as THREE from 'three';

const DUST_VERT = /* glsl */ `
uniform sampler2D uMap;
// 照片按 object-fit: cover 裁进画框：xy 缩放，zw 偏移
uniform vec4 uCrop;
uniform float uLod;
// 星尘云团的起点（打开时是那颗星，关上时是画框中心）、画框中心和大小，单位是 CSS 像素，y 朝上
uniform vec2 uFrom;
uniform vec2 uCenter;
uniform vec2 uSize;
uniform float uProgress;
uniform float uTime;
uniform float uScatter;
uniform float uPointPx;
uniform float uOpacity;
uniform float uLift;
uniform float uSwirl;
uniform float uGrid;
// 1 是从左往右擦（字）：左边的先飘起来；0 是照片：随机先后，上面的行先到
uniform float uSweep;
attribute vec4 aRnd;
attribute vec3 aScatter;
varying vec3 vColor;
varying float vAlpha;
float eio(float t) { return t < 0.5 ? 4.0 * t * t * t : 1.0 - pow(-2.0 * t + 2.0, 3.0) * 0.5; }
void main() {
  // 粒子在照片上的格点 (0..1)；每颗带随机延迟，上面的行先到
  vec2 g = position.xy;
  float delay = aRnd.x * mix(0.38, 0.1, uSweep) + (1.0 - g.y) * 0.1 * (1.0 - uSweep) + (1.0 - g.x) * 0.38 * uSweep;
  float t = clamp((uProgress - delay) / 0.52, 0.0, 1.0);
  float e = eio(t), inv = 1.0 - e;
  // 落点加不到一格的抖动，打散规则网格叠出来的横纹
  vec2 target = (g + (aRnd.yz - 0.5) * 0.9 / uGrid - 0.5) * uSize;
  vec2 local = mix(vec2(aScatter.x * 1.5, aScatter.y) * uScatter, target, e);
  // 漩涡：越接近成像越收敛
  float ang = inv * uSwirl * (0.6 + aRnd.y * 0.8), cs = cos(ang), sn = sin(ang);
  local = mat2(cs, sn, -sn, cs) * local;
  // 云团中心从起点沿一条弧线移到画框中心，每颗粒子的弧度不同，像一股被吸进去的星河
  vec2 dir = uCenter - uFrom;
  vec2 c = mix(uFrom, uCenter, e) + vec2(-dir.y, dir.x) * sin(3.14159265 * e) * (aRnd.z - 0.5) * 0.35;
  vec2 p = c + local;
  p += vec2(sin(uTime * 1.3 + aRnd.z * 31.0), cos(uTime * 1.1 + aRnd.w * 17.0)) * sin(3.14159265 * e) * uSize.y * 0.045;
  // 消散时像天灯一样上升
  p.y += inv * inv * uLift * (0.4 + aRnd.w);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 0.0, 1.0);
  // 纹理是预乘透明度上传的：除回去拿到本来的颜色；透明的地方（字的周围）没有星尘
  vec4 tex = textureLod(uMap, g * uCrop.xy + uCrop.zw, uLod);
  vec3 col = tex.a > 0.001 ? tex.rgb / tex.a : vec3(0.0);
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  // 原型在三维里飞，远处的粒子自然显小；这里在屏幕上飞，起点的粒子要小一些、淡一些才像星尘
  gl_PointSize = uPointPx * mix(0.3 + aRnd.w * 0.9, 0.8 + 0.4 * sqrt(lum), e);
  // 飞行中更亮、带一点玫瑰色；落定后暗一些，和下面烧出来的照片叠在一起
  vColor = mix(col * 1.5 + vec3(0.9, 0.62, 0.78) * 0.25, col * 0.62, e);
  vAlpha = uOpacity * mix(0.35 + 0.45 * aRnd.z, 1.0, e) * smoothstep(0.0, 0.05, uProgress) * smoothstep(0.08, 0.5, tex.a);
  // 擦字时，还没被擦到、没动起来的星尘先不画：那里的字还是清楚的，盖上一层星尘会一下子变得毛糙
  vAlpha *= mix(1.0, 1.0 - smoothstep(0.85, 1.0, e), uSweep);
}
`;

const DUST_FRAG = /* glsl */ `
varying vec3 vColor;
varying float vAlpha;
void main() {
  float d = length(gl_PointCoord - 0.5);
  gl_FragColor = vec4(min(vColor, vec3(1.0)), (1.0 - smoothstep(0.25, 0.5, d)) * vAlpha);
  #include <colorspace_fragment>
  // 画布按预乘透明度合成：颜色乘上透明度，叠加时颜色和透明度都累加
  gl_FragColor.rgb *= gl_FragColor.a;
}
`;

const PHOTO_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// 噪声溶解：阈值推进时边缘发光，像照片被星火点燃 / 熄灭（同原型的卡片着色器）
const PHOTO_FRAG = /* glsl */ `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec2 uSize;
uniform float uRadius;
uniform float uDissolve;
uniform float uSeed;
uniform vec3 uAccent;
uniform float uSweep;
varying vec2 vUv;
float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}
void main() {
  // 和面板的画框一样：只有上面两个角是圆的
  vec2 px = (vUv - 0.5) * uSize;
  float r = px.y > 0.0 ? uRadius : 0.0;
  vec2 q = abs(px) - uSize * 0.5 + r;
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
  float inside = 1.0 - smoothstep(-0.75, 0.75, d);
  float n = clamp((fbm(vUv * vec2(uSize.x / uSize.y, 1.0) * 4.5 + uSeed) - 0.2) / 0.55, 0.0, 1.0);
  // 擦字：烧的先后主要看横向位置（从左往右），噪声只留一点毛边
  n = mix(n, 1.0 - vUv.x, uSweep * 0.8);
  float th = uDissolve * 1.16 - 0.08;
  float vis = 1.0 - smoothstep(th - 0.035, th, n);
  float edge = (1.0 - smoothstep(th, th + 0.06, n)) * (1.0 - vis) * step(0.001, uDissolve) * step(uDissolve, 0.999);
  vec3 rim = mix(uAccent, vec3(1.0), 0.35);
  // 预乘上传的纹理除回本来的颜色；字的周围透明，烧的那道光也只在字上
  vec4 tex = texture2D(uMap, vUv * uCrop.xy + uCrop.zw);
  vec3 col = (tex.a > 0.001 ? tex.rgb / tex.a : vec3(0.0)) + edge * (rim * 1.3 + 0.28);
  gl_FragColor = vec4(min(col, vec3(1.0)), max(vis, edge) * inside * tex.a);
  #include <colorspace_fragment>
  gl_FragColor.rgb *= gl_FragColor.a;
}
`;

const smooth = (t: number) => t * t * (3 - 2 * t);
const inOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
const outCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));

export interface Point {
  x: number;
  y: number;
}

/** 一张能当纹理的图：已解码的图片，或者画好的 canvas（压测模式里只有颜色） */
export type DustImage = HTMLImageElement | HTMLCanvasElement;

interface Run {
  kind: 'open' | 'close' | 'wipe';
  t0: number;
  raf: number;
  from: () => Point | null;
  frame: () => DOMRect;
  onDone: () => void;
  revealed: boolean;
}

export class Stardust {
  private renderer: THREE.WebGLRenderer | null = null;
  private failed = false;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(0, 1, 0, -1, -10, 10);
  private readonly dust: THREE.ShaderMaterial;
  private readonly photo: THREE.ShaderMaterial;
  private readonly quad: THREE.Mesh;
  private readonly texture = new THREE.Texture();
  private readonly host: HTMLElement;
  private readonly grid: number;
  private run: Run | null = null;
  private image: DustImage | null = null;
  private lastFrom: Point = { x: 0, y: 0 };
  private size = '';

  constructor(host: HTMLElement, opts: { grid: number }) {
    this.host = host;
    this.grid = opts.grid;
    const G = this.grid, n = G * G, pos = new Float32Array(n * 3), rnd = new Float32Array(n * 4), sc = new Float32Array(n * 3);
    for (let y = 0, i = 0; y < G; y++) {
      for (let x = 0; x < G; x++, i++) {
        pos[i * 3] = (x + 0.5) / G;
        pos[i * 3 + 1] = (y + 0.5) / G;
        for (let k = 0; k < 4; k++) rnd[i * 4 + k] = Math.random();
        // 散布点：球壳里的随机点，越往外越稀
        const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, s = Math.sqrt(1 - u * u), rr = 0.35 + Math.cbrt(Math.random()) * 0.65;
        sc[i * 3] = Math.cos(th) * s * rr;
        sc[i * 3 + 1] = u * rr;
        sc[i * 3 + 2] = Math.sin(th) * s * rr;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aRnd', new THREE.BufferAttribute(rnd, 4));
    g.setAttribute('aScatter', new THREE.BufferAttribute(sc, 3));
    const tex = this.texture;
    tex.colorSpace = THREE.SRGBColorSpace;
    // 预乘上传：照片不透明，结果一样，还能绕开 Safari 不预乘上传时的坏点（见 galaxy.ts 的 canvasTexture）
    tex.premultiplyAlpha = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    const crop = new THREE.Vector4(1, 1, 0, 0), sizeV = new THREE.Vector2(1, 1);
    this.dust = new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: tex },
        uCrop: { value: crop },
        uLod: { value: 0 },
        uFrom: { value: new THREE.Vector2() },
        uCenter: { value: new THREE.Vector2() },
        uSize: { value: sizeV },
        uProgress: { value: 0 },
        uTime: { value: 0 },
        uScatter: { value: 100 },
        uPointPx: { value: 3 },
        uOpacity: { value: 1 },
        uLift: { value: 0 },
        uSwirl: { value: 2 },
        uGrid: { value: this.grid },
        uSweep: { value: 0 },
      },
      vertexShader: DUST_VERT,
      fragmentShader: DUST_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendSrcAlpha: THREE.OneFactor,
      blendDstAlpha: THREE.OneFactor,
    });
    this.photo = new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: tex },
        uCrop: { value: crop },
        uSize: { value: sizeV },
        uRadius: { value: 0 },
        uDissolve: { value: 0 },
        uSeed: { value: 0 },
        uAccent: { value: new THREE.Color() },
        uSweep: { value: 0 },
      },
      vertexShader: PHOTO_VERT,
      fragmentShader: PHOTO_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      premultipliedAlpha: true,
    });
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.photo);
    this.quad.frustumCulled = false;
    const points = new THREE.Points(g, this.dust);
    points.frustumCulled = false;
    points.renderOrder = 1;
    this.scene.add(this.quad, points);
  }

  get running(): boolean {
    return this.run !== null;
  }

  private ensure(): THREE.WebGLRenderer | null {
    if (this.renderer || this.failed) return this.renderer;
    try {
      const r = new THREE.WebGLRenderer({ alpha: true, antialias: false, premultipliedAlpha: true });
      r.setClearColor(0x000000, 0);
      r.domElement.className = 'fx-dust';
      r.domElement.hidden = true;
      r.domElement.setAttribute('aria-hidden', 'true');
      this.host.append(r.domElement);
      this.renderer = r;
    } catch {
      this.failed = true;
    }
    return this.renderer;
  }

  /** 照片裁进画框的方式和 object-fit: cover 一样；画框比例变了要重算 */
  private fit(rect: DOMRect): void {
    const img = this.image;
    if (!img) return;
    const iw = img instanceof HTMLImageElement ? img.naturalWidth : img.width, ih = img instanceof HTMLImageElement ? img.naturalHeight : img.height;
    const ia = iw / Math.max(1, ih), ba = rect.width / Math.max(1, rect.height);
    const sx = Math.min(1, ba / ia), sy = Math.min(1, ia / ba);
    (this.dust.uniforms.uCrop!.value as THREE.Vector4).set(sx, sy, (1 - sx) / 2, (1 - sy) / 2);
    // 每个格点覆盖几个纹素，就往上取几级 mipmap，免得闪烁
    this.dust.uniforms.uLod!.value = Math.max(0, Math.log2(Math.sqrt(sx * iw * sy * ih) / this.grid) - 0.5);
  }

  /** 换一张更清楚的图（打开到一半时清晰图到了） */
  setImage(img: DustImage): void {
    // 同一个 <img> 换了 src 也要重传
    this.image = img;
    // 尺寸变了的话 three.js 不会重新分配纹理存储（WebGL2 的 texStorage 尺寸固定），先释放再传
    this.texture.dispose();
    this.texture.image = img;
    this.texture.needsUpdate = true;
    if (this.run) this.fit(this.run.frame());
  }

  /**
   * 打开：from() 每帧给出那颗星现在在屏幕上的位置（镜头在飞，星也在动），frame() 给出画框的位置。
   * 清晰的照片烧完、该露出面板里的图片时调 onDone。做不了（没有 WebGL）时返回 false。
   */
  open(o: { image: DustImage; accent: string; radius: number; from: () => Point | null; frame: () => DOMRect; onDone: () => void }): boolean {
    return this.start('open', o);
  }

  /** 关上：照片在 rect 那里烧掉，星尘升起飘散 */
  close(o: { image: DustImage; accent: string; radius: number; rect: DOMRect }): boolean {
    const rect = o.rect;
    return this.start('close', { ...o, from: () => null, frame: () => rect, onDone: () => undefined });
  }

  /**
   * 擦掉一段字：image 是画好字的透明画布（最好是方的，格点横竖一样密），rect 是它在屏幕上的位置。
   * 从左往右烧过去，擦过的地方化成星尘升起。做不了（没有 WebGL）时返回 false
   */
  wipe(o: { image: DustImage; accent: string; rect: DOMRect }): boolean {
    const rect = o.rect;
    return this.start('wipe', { ...o, radius: 0, from: () => null, frame: () => rect, onDone: () => undefined });
  }

  private start(kind: Run['kind'], o: { image: DustImage; accent: string; radius: number; from: () => Point | null; frame: () => DOMRect; onDone: () => void }): boolean {
    this.finish();
    const r = this.ensure();
    if (!r) return false;
    this.setImage(o.image);
    (this.photo.uniforms.uAccent!.value as THREE.Color).set(o.accent);
    this.photo.uniforms.uRadius!.value = o.radius;
    this.photo.uniforms.uSeed!.value = Math.random() * 10;
    const rect = o.frame(), u = this.dust.uniforms;
    this.fit(rect);
    const from = o.from();
    this.lastFrom = from ?? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    // 原型：起点散布约为画框高度的 2.4 倍（三维里离镜头远，看上去不大）；这里打开时从一颗星涌出，云团小得多
    u.uScatter!.value = rect.height * (kind === 'open' ? 0.25 : kind === 'wipe' ? 0.8 : 1.2);
    u.uSwirl!.value = kind === 'open' ? 2.6 : kind === 'wipe' ? 1.2 : 1.6;
    u.uLift!.value = kind === 'open' ? 0 : rect.height * (kind === 'wipe' ? 0.9 : 1.3);
    u.uSweep!.value = this.photo.uniforms.uSweep!.value = kind === 'wipe' ? 1 : 0;
    u.uOpacity!.value = 1;
    this.photo.uniforms.uDissolve!.value = kind === 'open' ? 0 : 1;
    r.domElement.hidden = false;
    this.run = { kind, t0: performance.now(), raf: 0, from: o.from, frame: o.frame, onDone: o.onDone, revealed: false };
    this.frame();
    return true;
  }

  private frame(): void {
    const run = this.run, r = this.renderer;
    if (!run || !r) return;
    const w = this.host.clientWidth, h = this.host.clientHeight, key = `${w}x${h}`;
    if (key !== this.size) {
      this.size = key;
      r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      r.setSize(w, h);
      Object.assign(this.camera, { left: 0, right: w, top: 0, bottom: -h });
      this.camera.updateProjectionMatrix();
    }
    const t = (performance.now() - run.t0) / 1000, u = this.dust.uniforms, pu = this.photo.uniforms;
    const rect = run.frame(), cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    const from = run.from();
    if (from) this.lastFrom = from;
    let done = false;
    if (run.kind === 'open') {
      // 星尘 0.1 秒后开始凝聚，2 秒成像；1.9 秒起清晰照片烧出来；烧完露出面板里的图，星尘再淡去
      u.uProgress!.value = clamp01((t - 0.1) / 2.0);
      const burn = clamp01((t - 1.9) / 0.8);
      pu.uDissolve!.value = inOutSine(burn);
      u.uOpacity!.value = 1 - inOutSine(clamp01((t - 2.85) / 0.9));
      if (burn >= 1 && !run.revealed) {
        run.revealed = true;
        run.onDone();
      }
      done = t >= 3.75;
    } else if (run.kind === 'wipe') {
      // 1.4 秒从左往右烧过去；星尘跟着擦过的地方飘起，2.4 秒散完
      pu.uDissolve!.value = 1 - inOutSine(clamp01(t / 1.4));
      u.uProgress!.value = 1 - inOutSine(clamp01((t - 0.05) / 2.2));
      done = t >= 2.4;
    } else {
      // 照片 0.35 秒烧掉；星尘从成像倒退回散布点，一边升起
      pu.uDissolve!.value = 1 - outCubic(clamp01(t / 0.35));
      u.uProgress!.value = 1 - inOutSine(clamp01((t - 0.1) / 1.7));
      done = t >= 1.8;
    }
    (u.uFrom!.value as THREE.Vector2).set(this.lastFrom.x, -this.lastFrom.y);
    (u.uCenter!.value as THREE.Vector2).set(cx, -cy);
    (u.uSize!.value as THREE.Vector2).set(rect.width, rect.height);
    u.uTime!.value = t;
    // 原型：点径约为格距的 1.35 倍
    u.uPointPx!.value = (Math.max(rect.width, rect.height) / this.grid) * 1.35 * Math.min(window.devicePixelRatio || 1, 2);
    this.quad.position.set(cx, -cy, 0);
    this.quad.scale.set(rect.width, rect.height, 1);
    this.quad.visible = pu.uDissolve!.value > 0 && !(run.kind === 'open' && run.revealed);
    r.render(this.scene, this.camera);
    if (done) this.finish();
    else run.raf = requestAnimationFrame(() => this.frame());
  }

  /** 立刻结束：打开的话直接露出面板里的图 */
  finish(): void {
    const run = this.run;
    if (!run) return;
    this.run = null;
    cancelAnimationFrame(run.raf);
    if (this.renderer) {
      this.renderer.clear();
      this.renderer.domElement.hidden = true;
    }
    // 照片纹理不留在显存里，下次用时再传
    this.texture.dispose();
    if (!run.revealed) run.onDone();
  }
}
